<template>
  <div class="panel-container-20241114-161616">
    <div class="left-container">
      <el-calendar class="el-calendar-20241114-161616" v-model="activeDay">
        <template #date-cell="{ data }">
          <div class="day-text-container">{{ data.date.getDate() }}</div>
          <div class="point-symbol" :class="getPointClassName(data.date)"></div>
        </template>
      </el-calendar>
      <div class="day-state-desc-20241114-161616">
        <div class="point-symbol  rest"></div>
        <div class="text-container">{{ $str.getKey('策略控制/休息日策略') }}</div>
      </div>
      <div class="day-state-desc-20241114-161616">
        <div class="point-symbol work"></div>
        <div class="text-container">{{ $str.getKey('策略控制/工作日策略') }}</div>
      </div>
      <div class="day-state-desc-20241114-161616">
        <div class="point-symbol appointment"></div>
        <div class="text-container">{{ $str.getKey('策略控制/预约策略生效日期') }}</div>
      </div>

    </div>
    <!-- 右侧图表 -->
    <div class="right-container">
      <div class="top-info-container">
        <span class="info-text"> {{ $str.getKey('策略控制/日期') }}：{{ formattedActiveDay }} &nbsp;&nbsp; {{ $str.getKey('策略控制/执行策略') }}：{{ activeStrategyName }}</span>
        <div v-if="switching" class="radio-group-container">
          <el-radio-group v-model="inputStrategyType" :disabled="saving">
            <el-radio label="work">{{ $str.getKey('策略控制/工作日策略') }}</el-radio>
            <el-radio label="rest">{{ $str.getKey('策略控制/休息日策略') }}</el-radio>
          </el-radio-group>
          <div class="btn-container">
            <el-button :icon="CloseBold" text @click="cancelEdit" :disabled="saving" class="cancle">{{ $str.getKey('策略控制/取消') }}</el-button>
            <el-button :icon="Select" text type="success" @click="save" :loading="saving" class="save">{{ $str.getKey('策略控制/保存') }}</el-button>
          </div>
        </div>
        <el-button :icon="Switch" plain class="switch-btn" @click="onSwitchStrategy" v-else-if="hasStrategyData" :disabled="!checkPermi(['ops:strategyControl:config'])">{{ $str.getKey('策略控制/切换') }}</el-button>

      </div>
      <div class="chart-container">
        <v-chart v-if="hasStrategyData" :option="option" :autoresize="true"></v-chart>
        <div v-else class="empty-strategy-tip">
          <span>{{ $str.getKey('策略控制/该日期无执行策略') }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>

import {computed, ref, watch} from 'vue'
import VChart from 'vue-echarts'
import dayjs from "dayjs"
import {CloseBold, Select, Switch} from '@element-plus/icons-vue'
import * as strategyApi from '../api.js';
import {
  calculationStrategyType,
  createOption,
  strategyNameFromType,
  checkPermi,
} from "../utils.js"
import useAppStore from '@/store/modules/app'
import useUserStore from '@/store/modules/user'





const $str = window.$str;

const appStore = useAppStore()
const userStore = useUserStore()

const props = defineProps(['currentELinkData', 'customStrategys'])
const activeDay = ref(new Date())
const plans = ref([])
/** 选中日期真实执行过的策略（仅历史日期有值） */
const dayHistory = ref(null)
// 请求序号，用于丢弃快速切换日期时的过期响应
let planSeq = 0
let historySeq = 0
const formattedActiveDay = computed(() => {
  return dayjs(activeDay.value).format('YYYY-MM-DD')
})
/**
 * 从一份策略记录里取出选中日期该执行的时段数据
 * @param source 策略记录（预约策略 / 设备当前策略）
 * @param date 日期
 * @returns {{strategys: Array, strategy_type: 'work'|'rest'}|null}
 */
function slotsOf(source, date) {
  if (!source) return null
  const strategyType = getStrategyType(date)
  const strategys = strategyType === 'rest' ? source.strategys_rest : source.strategys_work
  return strategys?.length ? { strategys, strategy_type: strategyType } : null
}

/**
 * 选中日期实际执行的策略——图表、标题、空态的唯一数据源
 *
 *   今天之前 → 只信 /strategy/history/running。有返回就展示，没有返回就不展示，
 *              不做任何前端推算（不再用预约策略 / 设备当前策略兜底）。
 *   今天及之后 → 设备当前策略，被生效日 <= D 的最后一条预约策略覆盖。
 *              即：今天到下一个预约生效日之间都是今天的策略，之后换成该预约策略。
 *
 * 取不到可展示的时段数据时返回 null，上层显示「该日期无执行策略」。
 * @returns {{strategys: Array, strategy_type?: 'work'|'rest'}|null}
 */
const activeStrategy = computed(() => {
  const date = activeDay.value

  if (isPast(date)) {
    // 只认历史接口，返回什么展示什么
    return dayHistory.value?.strategys?.length ? dayHistory.value : null
  }

  // getStrategySource：生效日 <= D 的最后一条预约策略，没有则用设备当前策略
  return slotsOf(getStrategySource(date), date)
})

/** 有可展示的策略才画图表、才允许「切换」 */
const hasStrategyData = computed(() => !!activeStrategy.value)

const activeStrategyName = computed(() => {
  const strategy = activeStrategy.value
  if (!strategy) {
    return '--'
  }
  // 历史记录可能不带 strategy_type，回退到按 week_plan 推算
  return strategyNameFromType(strategy.strategy_type || getStrategyType(activeDay.value))
})

const switching = ref(false)
const inputStrategyType = ref('work')
const saving = ref(false)


/**
 * 图表配置项
 * @type {ComputedRef<*>}
 */
const option = computed(() => {
  return createOption(activeStrategy.value?.strategys || [], props.currentELinkData?.powerLimit)
})


const emit = defineEmits(['switch-strategy'])

/**
 * 监听日期选择，更新当前月份的特殊计划
 */
watch(activeDay, async () => {
  await getPlan()
  await getDayHistory()
}, {
  immediate: true
})

/**
 * 监听 elink_id 变化（切换电站或切换 eLink 时），清空缓存并重新拉取数据
 */
watch(() => props.currentELinkData?.elink_id, (newId, oldId) => {
  if (newId && newId !== oldId) {
    // 清空旧电站的缓存数据，避免短暂显示错误内容
    plans.value = []
    dayHistory.value = null
    // 序号自增，丢弃旧电站尚在飞行中的请求
    planSeq++
    historySeq++
    // 重新拉取当前电站的数据
    getPlan()
    getDayHistory()
  }
})

function getDateArea() {
  let begin_date = dayjs(activeDay.value).startOf('month').format("YYYY-MM-DD 00:00:00")
  let end_date = dayjs(activeDay.value).endOf('month').format("YYYY-MM-DD 23:59:59")

  return {
    begin_date,
    end_date
  }

}

async function getPlan() {
  if (!props.currentELinkData) {
    return
  }
  const elinkId = props.currentELinkData.elink_id
  const {begin_date, end_date} = getDateArea()

  const seq = ++planSeq
  try {
    let res = await strategyApi.getPlan(elinkId, begin_date, end_date)
    // 快速切换月份时，丢弃过期响应
    if (seq !== planSeq) return
    plans.value = res.data
  } catch (error) {
    if (seq !== planSeq) return
    console.warn(error)
  }
}


/**
 * 归一化「查询某日策略」接口的返回值
 *
 * 接口语义是「返回该日期当天生效中的那条策略记录」，因此返回体自带的时间
 * （created_time 等）早于所查日期是正常的——策略一直沿用未被修改。这里
 * 不做日期校验，否则会把唯一正确的答案当成脏数据丢掉。
 *
 * 返回结构存在多种形态，无法识别时返回 null，上层按「当天无策略」处理
 * @param data 接口返回的 data
 * @returns {{strategys: Array, strategy_type?: string}|null}
 */
/**
 * 归一化「历史运行策略」接口的返回值
 *
 * 接口返回什么就展示什么，不做日期校验、不与其它数据源比较；
 * 结构无法识别（或没有时段数据）时返回 null，上层显示「该日期无执行策略」
 * @param data 接口返回的 data
 * @returns {{strategys: Array, strategy_type?: string}|null}
 */
function normalizeDayHistory(data) {
  if (!data || typeof data !== 'object') {
    return null
  }
  // 形态一：直接返回当日策略时段数组
  if (Array.isArray(data)) {
    return data.length ? { strategys: data } : null
  }

  const strategyType = data.strategy_type
  // 形态二：完整策略记录，按 strategy_type 取对应时段
  if (strategyType === 'work' && Array.isArray(data.strategys_work)) {
    return { strategys: data.strategys_work, strategy_type: 'work' }
  }
  if (strategyType === 'rest' && Array.isArray(data.strategys_rest)) {
    return { strategys: data.strategys_rest, strategy_type: 'rest' }
  }
  // 形态三：时段数组挂在其它字段上
  // running_strategy：/strategy/history/running 当日实际运行时段，历史策略优先取它
  // elink_strategy：同接口返回的设备下发时段，running_strategy 缺失时兜底
  // strategy：实测 /strategy/history/elink 用的是单数
  for (const key of ['running_strategy', 'elink_strategy', 'strategys_current', 'strategys', 'strategy_list', 'strategy']) {
    if (Array.isArray(data[key]) && data[key].length) {
      return { strategys: data[key], strategy_type: strategyType }
    }
  }
  return null
}

/**
 * 拉取选中日期真实执行过的历史策略
 *
 * 仅历史日期需要查：今天及以后由设备当前策略 + 预约策略推算，无需查历史
 */
async function getDayHistory() {
  const seq = ++historySeq
  dayHistory.value = null

  const elinkId = props.currentELinkData?.elink_id
  if (!elinkId || !isPast(activeDay.value)) {
    return
  }

  try {
    const res = await strategyApi.getRunningHistory(elinkId, formattedActiveDay.value)
    if (seq !== historySeq) return
    dayHistory.value = normalizeDayHistory(res?.data)
  } catch (error) {
    if (seq !== historySeq) return
    console.warn(error)
  }
}


/**
 * 预约策略按生效日期升序排列
 * 不能依赖接口返回顺序，"最后一条生效"的判断需要确定的次序
 */
const sortedCustomStrategys = computed(() => {
  return [...(props.customStrategys || [])]
    .filter(item => item?.execution_time)
    .sort((a, b) => dayjs(a.execution_time).valueOf() - dayjs(b.execution_time).valueOf())
})

/**
 * 查询今天及未来生效中的预约策略：生效日 <= D 且 >= 今天，取最后一条
 *
 * 预约一旦生效就持续执行到下一条预约生效为止，因此取「最后一条」而非「当天那条」：
 * 10 天后有预约时，今天~第 9 天都取不到它（用设备当前策略），第 10 天起才命中。
 * @param date 日期
 * @returns {object|undefined}
 */
function getAppointmentForFuture(date) {
  const today = dayjs().startOf('day').valueOf()
  const day = dayjs(date).startOf('day').valueOf()
  return sortedCustomStrategys.value
    .filter(item => {
      const t = dayjs(item.execution_time).startOf('day').valueOf()
      return t >= today && t <= day
    })
    .at(-1)
}

/**
 * 是否为今天
 * @param date 日期
 * @returns {boolean}
 */
function isToday(date) {
  return dayjs(date).isSame(dayjs(), 'day')
}

/**
 * 是否为已过去的日期（不含今天）
 * @param date 日期
 * @returns {boolean}
 */
function isPast(date) {
  return dayjs(date).startOf('day').valueOf() < dayjs().startOf('day').valueOf()
}

/**
 * 获取指定日期的策略数据来源：未来生效的预约策略优先，否则用设备当前策略
 *
 * currentELinkData 是后端下发的设备当前实际执行策略，已包含此前所有变更的结果，
 * 因此「今天及此前」一律以它为准，日历与主页面口径一致
 * @param date 日期
 * @returns {object|undefined}
 */
function getStrategySource(date) {
  return getAppointmentForFuture(date) || props.currentELinkData
}

/**
 * 查询某天是否被手工切换过（日历「切换」按钮写入的特殊计划）
 * @param date 日期
 * @returns {object|undefined}
 */
function getPlanForDate(date) {
  const formattedDate = dayjs(date).format('YYYY-MM-DD')
  return (plans.value || []).find(
    plan => dayjs(plan.strategy_date).format('YYYY-MM-DD') === formattedDate
  )
}

/**
 * 获取每天对应的策略:工作日|休息日
 *
 * 优先级：手工切换(plans) > 今天用后端 strategy_type > 前端按 week_plan 推算
 * @param date 日期
 * @returns {'work'|'rest'}
 */
function getStrategyType(date) {
  // 今天：无手工切换时以后端下发的实际执行策略为准
  if (isToday(date) && !getPlanForDate(date) && props.currentELinkData?.strategy_type) {
    return props.currentELinkData.strategy_type
  }
  return calculationStrategyType(plans.value, date, getStrategySource(date)?.week_plan);
}

function getPointClassName(date) {
  // 预约策略的生效日期当天，单独高亮
  const formattedDate = dayjs(date).format('YYYY-MM-DD')
  if (sortedCustomStrategys.value.some(item => dayjs(item.execution_time).format('YYYY-MM-DD') === formattedDate)) {
    return 'appointment'
  }
  return getStrategyType(date)
}


function getSimpleName(date) {
  let texts = {
    work: '工',
    rest: '休',
  }
  return texts[getStrategyType(date)]
}


function onSwitchStrategy() {
  inputStrategyType.value = getStrategyType(activeDay.value)
  switching.value = true
}

function cancelEdit() {
  switching.value = false
}

function save() {
  saving.value = true
  const postData = {
    id: props.currentELinkData.id,
    strategy_date: formattedActiveDay.value,
    strategy_type: inputStrategyType.value,
    elink_id: props.currentELinkData.elink_id,
    station_id: appStore.stationId,
    station_name: appStore.stationName,
    opt_user: userStore.name,
  }
  strategyApi.updatePlan(postData).then(() => {
    switching.value = false
    window.dispatchNotify({
      type: 'success',
      message: $str.getKey('策略控制/切换成功')
    });
    emit('switch-strategy')
    // 切换后再次获取一下特殊策略
    getPlan()
  }).catch(e => {
    console.warn(e)
  }).finally(() => {
    saving.value = false
  })
}

getPlan()


</script>

<style lang="scss" scoped>
.panel-container-20241114-161616 {
  height: 100%;
  display: flex;
  gap: 10px;
  .left-container {
    width: 26%;
    flex: 1 1 26%;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
  .right-container {
    width: 74%;
    background: #FFFFFF;
    border-radius: 6px;
    flex: 1 1 74%;
    display: flex;
    flex-direction: column;
    .top-info-container {
      height: 60px;
      display: flex;
      align-items: center;
      padding-left: 16px;
      gap: 16px;
      .info-text {
        font-family: PingFang-SC, PingFang-SC;
        font-weight: 500;
        font-size: 16px;
        color: #2E2E2E;
        line-height: 23px;
        text-align: left;
      }
      .radio-group-container {
        display: flex;
        align-items: center;
        gap: 40px;
        .btn-container {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 20px;
          .save, .cancle {
            border: 1px solid currentColor;
          }
        }
      }
      .switch-btn.el-button.is-plain {
        // background: #21263B;
        // color: #5B80FF;
        // box-shadow: inset 0px 0px 16px 8px #5B80FF66;
        // border: 1px solid #5B80FFAA;
        // border-radius: 4px;
        // &:hover {
        //   background: #5B80FF33;
        // }
      }
    }
    .chart-container {
      height: 100%;
      flex: 1;

      .empty-strategy-tip {
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #999;
        font-family: "HarmonyOS Sans SC";
        font-size: 14px;
      }
    }

  }
}

</style>

<style lang="scss">
.panel-container-20241114-161616 {
  --rest-color: #21B872;
  --work-color: #596EFF;
  --appointment-color: #FFDD32;

  
  .el-calendar-20241114-161616 {
    // background-color: #FFFFFF;
    border-radius: 6px;
    
    .el-calendar__body {
      .el-calendar-table {
        td {
          // ------ 设置cell的基础样式 ------
          .el-calendar-day {
            & {
              width: 56px;
              height: 56px;
              display: inline-flex;
              flex-direction: column;
              justify-content: center;
              align-items: center;
              gap: 4px;
            }
  
            .day-text-container {
              width: 28px;
              height: 28px;
              display: flex;
              justify-content: center;
              align-items: center;
              border-radius: 50%;
            }
            
            // 日历单元下的指示圆点
            .point-symbol {
                width: 8px;
                height: 8px;
                border-radius: 50%;
                margin: 0 8px;
                &.work {
                  background-color: var(--work-color);
                }
                &.rest {
                  background-color: var(--rest-color);
                }
                &.appointment {
                  background-color: var(--appointment-color);
                }
            }
          }

          // ------ 设置选中时的样式 ------
          &.is-selected {
            .day-text-container {
              background-color: var(--el-calendar-cell-selected-bg-color);
            }
          }

          // ------ 设置鼠标悬停时的样式 ------
          &:hover {
            // 自定义cell的hover样式时，先去除已有背景色的干扰
            .el-calendar-day:hover {
              background-color: transparent !important;
            }
            .day-text-container {
              background-color: var(--el-calendar-cell-hover-bg-color) !important;
            }
          }
        }
      }



    }
  }

  .day-state-desc-20241114-161616 {
    flex: 1 1 60px;
    background-color: #FFFFFF;
    border-radius: 6px;
    display: flex;
    justify-content: center;
    align-items: center;

    .point-symbol {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      margin: 0 8px;
      &.work {
        background-color: var(--work-color);
      }
      &.rest {
        background-color: var(--rest-color);
      }
      &.appointment {
        background-color: var(--appointment-color);
      }
    }
    .text-container {
      width: 130px;
      font-family: PingFang-SC, PingFang-SC;
      font-size: 14px;
      color: #2E2E2E;
      text-align: left;
    }

  }
}

</style>


